type HeadersLike = HeadersInit | undefined;

const isAbortError = (error: unknown): error is DOMException | { name?: string; code?: string } => {
  if (!error || typeof error !== 'object') return false;
  const name = (error as { name?: string }).name;
  const code = (error as { code?: string }).code;
  return name === 'AbortError' || code === 'ABORT';
};

const normalizeHeaders = (headers: HeadersLike): Headers => {
  if (headers instanceof Headers) {
    return new Headers(headers);
  }
  if (Array.isArray(headers)) {
    return new Headers(headers);
  }
  return new Headers(headers ?? undefined);
};

const attachNoStore = (headers: Headers): Headers => {
  headers.set('Cache-Control', 'no-store');
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  return headers;
};

const SESSION_EXPIRED_REASON = 'session_expired';

const redirectToSessionExpiredLogin = (message?: string, callbackUrl?: string) => {
  if (typeof window === 'undefined') return;
  try {
    const current = callbackUrl ?? window.location.href;
    const encodedCallback = encodeURIComponent(current);
    const encodedMessage = encodeURIComponent(
      message?.trim() || 'Session expired. Please log in again.',
    );
    window.location.href = `/login?reason=${SESSION_EXPIRED_REASON}&callbackUrl=${encodedCallback}&message=${encodedMessage}`;
  } catch {
    window.location.href = `/login?reason=${SESSION_EXPIRED_REASON}`;
  }
};

export async function handleUnauthorized(reason?: string): Promise<void> {
  if (typeof window === 'undefined') return;
  const message = reason && reason !== SESSION_EXPIRED_REASON ? reason : undefined;
  const currentUrl = window.location.href;
  const encodedCallback = encodeURIComponent(currentUrl);
  const encodedMessage = encodeURIComponent(
    message?.trim() || 'Session expired. Please log in again.',
  );
  const callbackUrl = `/login?reason=${SESSION_EXPIRED_REASON}&callbackUrl=${encodedCallback}&message=${encodedMessage}`;
  try {
    const { signOut } = await import('next-auth/react');
    await signOut({ redirect: true, callbackUrl });
  } catch {
    redirectToSessionExpiredLogin(message, currentUrl);
  }
};

const mergeSignals = (controller: AbortController, others: AbortSignal[]): (() => void) => {
  if (!others.length) {
    return () => undefined;
  }
  const listeners = others.map((signal) => {
    const handler = () => {
      try {
        if (!controller.signal.aborted) {
          controller.abort(signal.reason);
        }
      } catch {
        // ignore
      }
    };
    signal.addEventListener('abort', handler, { once: true });
    return { signal, handler };
  });
  return () => {
    listeners.forEach(({ signal, handler }) => {
      signal.removeEventListener('abort', handler);
    });
  };
};

export interface ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
}

export const isApiError = (value: unknown): value is ApiError =>
  !!value &&
  typeof value === 'object' &&
  'code' in value &&
  typeof (value as { code?: unknown }).code === 'string' &&
  'status' in value &&
  typeof (value as { status?: unknown }).status === 'number';

const createApiError = (
  status: number,
  code: string,
  message: string,
  details: unknown,
): ApiError => {
  const error = new Error(message) as ApiError;
  error.name = 'ApiError';
  error.status = status;
  error.code = code;
  error.details = details;
  return error;
};

type FetchOptions = RequestInit & { next?: { revalidate?: number } };

export async function fetchJson<T = unknown>(
  url: string,
  init: FetchOptions = {},
  signal?: AbortSignal,
): Promise<T> {
  const controller = new AbortController();
  const signals: AbortSignal[] = [];
  if (init.signal) signals.push(init.signal);
  if (signal) signals.push(signal);

  if (signals.some((s) => s.aborted)) {
    throw new DOMException('Aborted', 'AbortError');
  }

  const headers = attachNoStore(normalizeHeaders(init.headers));
  const requestInit: FetchOptions = {
    ...init,
    signal: controller.signal,
    cache: 'no-store',
    next: { revalidate: 0, ...(init.next ?? {}) },
    headers,
  };

  const detach = mergeSignals(controller, signals);

  try {
    const response = await fetch(url, requestInit);
    const text = await response.text();
    const body = text ? (() => {
      try {
        return JSON.parse(text);
      } catch {
        return null;
      }
    })() : null;

    if (!response.ok) {
      const code =
        typeof (body as { code?: unknown } | null)?.code === 'string' &&
        ((body as { code?: string }).code ?? '').trim()
          ? ((body as { code?: string }).code as string)
          : `HTTP_${response.status}`;
      const message =
        typeof (body as { message?: unknown } | null)?.message === 'string' &&
        ((body as { message?: string }).message ?? '').trim()
          ? ((body as { message?: string }).message as string)
          : response.statusText || 'Request failed';
      if (response.status === 401) {
        let sessionMessage: string | undefined;
        if (body && typeof (body as { message?: unknown }).message === 'string') {
          sessionMessage = (body as { message?: string }).message;
        } else if (text) {
          try {
            const parsed = JSON.parse(text) as { message?: unknown };
            if (typeof parsed?.message === 'string') {
              sessionMessage = parsed.message;
            }
          } catch {
            // ignore parse failures
          }
        }
        await handleUnauthorized(sessionMessage);
      }
      throw createApiError(response.status, code, message, body);
    }

    return (body as T) ?? ({} as T);
  } catch (error) {
    if (isAbortError(error)) {
      throw new DOMException('Aborted', 'AbortError');
    }
    throw error;
  } finally {
    detach();
  }
}

type AbortableFetcher<T> = (key: string, signal: AbortSignal) => Promise<T>;

export function makeAbortable<T>(fetcher: AbortableFetcher<T>) {
  const controllers = new Map<string, AbortController>();

  return async (key: string): Promise<T> => {
    const existing = controllers.get(key);
    if (existing) {
      try {
        existing.abort();
      } catch {
        // ignore abort errors from prior controller
      }
    }

    const controller = new AbortController();
    controllers.set(key, controller);

    try {
      return await fetcher(key, controller.signal);
    } finally {
      const current = controllers.get(key);
      if (current === controller) {
        controllers.delete(key);
      }
    }
  };
}

export const abortSignals = {
  isAbortError,
};
